<template>
  <ExtendedModal
    :open="isOpen"
    :aria-label="shown ? i18n(shown.label) : undefined"
    @close="closeView()"
  >
    <DetailLayout v-if="shown" @close="closeView()">
      <template #header>
        <h2 class="raw-heading-2">{{ i18n(shown.label) }}</h2>
      </template>

      <component :is="CONTENT[shown.id]" />
    </DetailLayout>
  </ExtendedModal>
</template>

<script setup lang="ts">
import { type Component, computed, ref, watch } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'

import DetailLayout from '~/scopes/campaign/components/UI/DetailLayout.vue'
import ExtendedModal from '~/scopes/campaign/components/UI/ExtendedModal.vue'
import ContributorsDoc from '~/scopes/campaign/components/footer/docs/ContributorsDoc.vue'
import PressKitDoc from '~/scopes/campaign/components/footer/docs/PressKitDoc.vue'
import SourcesDoc from '~/scopes/campaign/components/footer/docs/SourcesDoc.vue'
import { useModalState } from '~/scopes/campaign/composables/useModalState'
import {
  type FooterDoc,
  type FooterDocId,
  footerDocById,
} from '~/scopes/campaign/data/footer-docs'

// The single surface behind all three footer documents — the state adapter `TakeActionModal`
// already is, one level thinner: there is no route form of these, only `?view=<doc id>`.
// `DetailLayout` supplies the header, the ✕ and the inner Lenis scrollport; each document
// brings only its body.
//
// No tier gate: the footer row is on screen at every width, so a link that opened nothing on
// phone would be a dead control. `ExtendedModal` already carries the narrower phone gutter it
// grew for take action.
//
// No `ContentSwap` either, unlike the article reader: the modal covers the footer that opens
// it, so one document can never swap to another while open.
const i18n = useI18n()
const { activeView, closeView } = useModalState()

const CONTENT: Record<FooterDocId, Component> = {
  sources: SourcesDoc,
  contributors: ContributorsDoc,
  'press-kit': PressKitDoc,
}

// One lookup answers both "which document" and "is this `?view=` value a document at all"
// — `footerDocById` returns null for `article` / `action` just as it does for a stale id.
const doc = computed(() => footerDocById(activeView.value))
const isOpen = computed(() => doc.value !== null)

// What the card renders, which is not the same as which document is open: closing drops
// `?view` immediately, so binding the body to `doc` would empty the card — title, ✕ and all —
// on the first frame of a 400ms fade. This holds the last resolved document through the
// leave, and is only ever replaced by another one. Seeded rather than left null, so a
// deep-linked document isn't an open card with nothing in it.
const shown = ref<FooterDoc | null>(doc.value)
watch(doc, (value) => {
  if (value) shown.value = value
})
</script>
